import { useEffect, useState } from 'react';
import { Button, Group } from '@mantine/core';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { CrmColumnFields } from './crm-column-fields';
import type { CrmField } from './crm-column-fields';
import type { CrmColumnConfig } from './crm-config';

type PlatformConfigFormProps = {
  config: CrmColumnConfig;
  onConfigChange: (config: CrmColumnConfig) => void;
};

function usePlatformTables() {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{ key: number; tables: string[]; error: string | null } | null>(null);

  useEffect(() => {
    let cancelled = false;

    void api['crm-integrations'].platform.tables.get().then(({ data, error }) => {
      if (cancelled) return;

      setResult(
        data
          ? { key: attempt, tables: data.tables, error: null }
          : { key: attempt, tables: [], error: getErrorMessage(error, 'Unable to load MALA Platform tables') },
      );
    });

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const loaded = result?.key === attempt ? result : null;

  return {
    tables: loaded?.tables ?? [],
    error: loaded?.error ?? null,
    isLoading: loaded === null,
    retry: () => setAttempt((a) => a + 1),
  };
}

function usePlatformFields(table: string) {
  const [result, setResult] = useState<{ key: string; fields: CrmField[] } | null>(null);

  useEffect(() => {
    if (!table) return;

    let cancelled = false;

    void api['crm-integrations'].platform.fields.get({ query: { table } }).then(({ data }) => {
      if (cancelled) return;

      setResult({ key: table, fields: data?.fields ?? [] });
    });

    return () => {
      cancelled = true;
    };
  }, [table]);

  const loaded = result?.key === table ? result : null;

  return { fields: table ? (loaded?.fields ?? null) : null, isLoading: Boolean(table) && loaded === null };
}

export function PlatformConfigForm({ config, onConfigChange }: PlatformConfigFormProps) {
  const { tables, error: tablesError, isLoading: isLoadingTables, retry } = usePlatformTables();
  const { fields, isLoading: isLoadingFields } = usePlatformFields(config.tableName);

  return (
    <>
      {tablesError && (
        <Group align="flex-start" gap="sm" wrap="nowrap">
          <div style={{ flex: 1 }}>
            <ErrorAlert message={tablesError} />
          </div>
          <Button variant="subtle" size="compact-sm" onClick={retry}>
            Retry
          </Button>
        </Group>
      )}

      <CrmColumnFields
        config={config}
        onConfigChange={onConfigChange}
        tables={tables}
        fields={fields}
        isLoadingTables={isLoadingTables}
        isLoadingFields={isLoadingFields}
      />
    </>
  );
}
